import React, { useState } from 'react';
import styled from 'styled-components';
import { LeagueInput as LeagueInputType } from '../services/fantasyApi';
import { ArrowLeft, Info, ChevronRight, ChevronLeft, X, ExternalLink } from 'lucide-react';
import { theme } from '../styles/theme';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  padding-top: 60px;
  
  @media (max-width: 768px) {
    padding-top: 50px;
  }
`;

const Container = styled.div`
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 32px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  font-family: ${theme.typography.fontFamily};
  overflow: hidden;
  
  &::before {
    content: '🏈';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-15deg);
    font-size: 200px;
    opacity: 0.03;
    z-index: 0;
    user-select: none;
    pointer-events: none;
  }
  
  /* Ensure all child content is above the background icon */
  > * {
    position: relative;
    z-index: 1;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: ${theme.typography.weights.semibold};
  color: white;
  font-size: ${theme.typography.sizes.base};
  font-family: ${theme.typography.fontFamily};
`;

const Input = styled.input`
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.2s;
  background: rgba(255, 255, 255, 0.7);
  color: #374151;
  backdrop-filter: blur(10px);
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
    background: rgba(255, 255, 255, 0.85);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const ExampleBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  
  strong {
    color: #fbbf24;
    font-weight: 600;
    background: rgba(251, 191, 36, 0.2);
    padding: 2px 4px;
    border-radius: 3px;
  }
`;

const ExampleText = styled.div`
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const HintText = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
  margin-top: 8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const Button = styled.button`
  padding: 16px 24px;
  background: rgba(99, 102, 241, 0.9);
  backdrop-filter: blur(20px);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  &:hover:not(:disabled) {
    background: rgba(79, 70, 229, 0.9);
    border-color: rgba(79, 70, 229, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
  }
  
  &:disabled {
    background: rgba(209, 213, 219, 0.6);
    color: rgba(107, 114, 128, 0.8);
    border-color: rgba(209, 213, 219, 0.3);
    cursor: not-allowed;
    transform: none;
    opacity: 0.6;
  }
`;

const DemoButton = styled.button`
  padding: 12px 20px;
  background: rgba(40, 167, 69, 0.2);
  color: white;
  border: 1px solid rgba(40, 167, 69, 0.3);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(40, 167, 69, 0.3);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  position: relative;
`;

const Title = styled.h2`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: white;
  margin: 0;
  flex: 1;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  backdrop-filter: blur(16px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.2);
  }
`;

const OrDivider = styled.div`
  text-align: center;
  margin: 20px 0;
  position: relative;
  color: rgba(255, 255, 255, 0.7);
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
  
  span {
    background: transparent;
    padding: 0 16px;
    position: relative;
  }
`;


const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  max-width: 600px;
  width: 90%;
  max-height: 75vh;
  padding: 1.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
    max-height: 70vh;
    width: 95%;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: white;
  font-size: 1.5rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const CarouselTrack = styled.div<{ $currentIndex: number }>`
  display: flex;
  transition: transform 0.3s ease;
  transform: translateX(-${props => props.$currentIndex * 100}%);
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const ModalImage = styled.img`
  width: 100%;
  height: auto;
  max-height: calc(75vh - 200px);
  object-fit: contain;
  display: block;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    max-height: calc(70vh - 180px);
  }
`;

const ImageCaption = styled.div`
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  width: 100%;
`;

const CarouselButton = styled.button<{ $direction: 'prev' | 'next' }>`
  position: absolute;
  top: 50%;
  ${props => props.$direction === 'prev' ? 'left: 10px;' : 'right: 10px;'}
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(10px);
  font-size: 20px;
  z-index: 2;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.05);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 1rem;
`;

const CarouselDot = styled.button<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.4)'};
  transition: background 0.3s ease;
  border: none;
  padding: 0;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.6)'};
  }
`;

const HowToFindButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.link};
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: 8px;
  transition: color 0.2s;
  
  &:hover {
    color: #6366f1;
  }
`;

interface Props {
  onSubmit: (input: LeagueInputType) => void;
  loading?: boolean;
  platform?: 'sleeper' | 'espn';
  onBack?: () => void;
}

export const SimpleLeagueInput: React.FC<Props> = ({ onSubmit, loading = false, platform = 'sleeper', onBack }) => {
  const [leagueId, setLeagueId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Parse ESPN URL to extract league ID
  const parseESPNUrl = (input: string): string => {
    const trimmedInput = input.trim();
    
    // If it's already just a number, return it
    if (/^\d+$/.test(trimmedInput)) {
      return trimmedInput;
    }
    
    // Try to extract leagueId from ESPN URL
    try {
      // Check if it contains fantasy.espn.com
      if (trimmedInput.includes('fantasy.espn.com') || trimmedInput.includes('espn.com')) {
        // Try to parse as URL
        const url = new URL(trimmedInput.includes('http') ? trimmedInput : `https://${trimmedInput}`);
        const params = new URLSearchParams(url.search);
        const leagueIdParam = params.get('leagueId');
        
        if (leagueIdParam) {
          return leagueIdParam;
        }
        
        // Also check path for patterns like /football/league/1234567
        const pathMatch = url.pathname.match(/\/league\/(\d+)/);
        if (pathMatch) {
          return pathMatch[1];
        }
      }
    } catch (e) {
      // If URL parsing fails, return the original input
    }
    
    return trimmedInput;
  };

  // Parse Sleeper URL to extract league ID
  const parseSleeperUrl = (input: string): string => {
    const trimmedInput = input.trim();
    
    // If it's already just a number, return it
    if (/^\d+$/.test(trimmedInput)) {
      return trimmedInput;
    }
    
    // Try to extract league ID from Sleeper URL
    // Pattern: sleeper.com/leagues/784462448236618752/...
    const match = trimmedInput.match(/leagues\/(\d+)/);
    if (match) {
      return match[1];
    }
    
    return trimmedInput;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!leagueId.trim()) return;
    
    // Parse the input based on platform
    const parsedId = platform === 'espn' 
      ? parseESPNUrl(leagueId)
      : parseSleeperUrl(leagueId);
    
    onSubmit({
      platform,
      leagueId: parsedId,
      season: new Date().getFullYear()
    });
  };

  const handleDemo = () => {
    // Use a known public Sleeper league for demo
    onSubmit({
      platform: 'sleeper',
      leagueId: '784462448236618752', // Demo league ID
      season: 2025
    });
  };

  const espnData = [
    {
      image: '/img/espn-1.jpg',
      caption: "Open up your ESPN Fantasy App and tap the 'ESPN Fantasy' logo at the top with the arrow and select your league."
    },
    {
      image: '/img/espn-2.jpg',
      caption: "Go to the League tab, and select League info"
    },
    {
      image: '/img/espn-3.jpg',
      caption: "League ID will be in your basic settings. Copy that number"
    }
  ];

  const sleeperData = [
    {
      image: '/img/sleeper-1.jpg',
      caption: "Find your league at the top of the home page on Sleeper and go to Settings, tap on General"
    },
    {
      image: '/img/sleeper-3.jpg',
      caption: "At the very bottom there is a league ID, copy it"
    }
  ];

  const imageData = platform === 'espn' ? espnData : sleeperData;

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev < imageData.length - 1 ? prev + 1 : prev
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev > 0 ? prev - 1 : prev
    );
  };

  const handleModalOpen = () => {
    setCurrentImageIndex(0);
    setShowModal(true);
  };

  return (
    <>
      <Wrapper>
        <Container>
          <Header>
            {onBack && (
              <BackButton onClick={onBack} aria-label="Go back">
                <ArrowLeft size={20} />
              </BackButton>
            )}
            <Title>Enter League Info</Title>
            <div style={{ width: '40px' }} /> {/* Spacer for centering */}
          </Header>
          <Form onSubmit={handleSubmit}>
        <InputGroup>
          <Label htmlFor="leagueId">League ID</Label>
          <Input
            id="leagueId"
            type="text"
            value={leagueId}
            onChange={(e) => setLeagueId(e.target.value)}
            placeholder={platform === 'espn' 
              ? "Paste ESPN URL or League ID"
              : "Paste Sleeper URL or League ID"}
            required
          />
          <ExampleBox>
            <ExampleText><Info size={16} /> Hint:</ExampleText>
            <HintText>
              {platform === 'espn' 
                ? "You can login to your league on this browser and copy and paste the league URL once you're logged on, or you can get it from the app"
                : "You can login to your league on this browser and copy and paste the league URL once you're logged on, or you can get it from the app"}
            </HintText>
            {platform === 'espn' 
              ? (
                <>
                  <div style={{ marginTop: '8px', fontSize: '13px' }}>
                    • Full URL: fantasy.espn.com/football/league?leagueId=<strong>1234567</strong>
                  </div>
                  <div style={{ fontSize: '13px' }}>
                    • Just ID: <strong>1234567</strong>
                  </div>
                </>
              )
              : (
                <>
                  <div style={{ marginTop: '8px', fontSize: '13px' }}>
                    • Full URL: sleeper.com/leagues/<strong>784462448236618752</strong>/team
                  </div>
                  <div style={{ fontSize: '13px' }}>
                    • Just ID: <strong>784462448236618752</strong>
                  </div>
                </>
              )}
          </ExampleBox>
          <HowToFindButton type="button" onClick={handleModalOpen}>
            <ExternalLink size={14} style={{ marginRight: '4px' }} />
            How to find in app
          </HowToFindButton>
        </InputGroup>

        <Button type="submit" disabled={loading || !leagueId.trim()}>
          {loading ? 'Loading...' : 'Next'}
        </Button>
      </Form>

        </Container>
      </Wrapper>

    <Modal $show={showModal}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            How to Find Your {platform === 'espn' ? 'ESPN' : 'Sleeper'} League ID
          </ModalTitle>
          <CloseButton onClick={() => setShowModal(false)}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        <CarouselContainer>
          <CarouselButton 
            $direction="prev" 
            onClick={prevImage}
            disabled={currentImageIndex === 0}
          >
            <ChevronLeft size={20} />
          </CarouselButton>
          <CarouselTrack $currentIndex={currentImageIndex}>
            {imageData.map((item, index) => (
              <CarouselSlide key={index}>
                <ModalImage src={item.image} alt={`Step ${index + 1}`} />
                <ImageCaption>{item.caption}</ImageCaption>
              </CarouselSlide>
            ))}
          </CarouselTrack>
          <CarouselButton 
            $direction="next" 
            onClick={nextImage}
            disabled={currentImageIndex === imageData.length - 1}
          >
            <ChevronRight size={20} />
          </CarouselButton>
        </CarouselContainer>
        <CarouselDots>
          {imageData.map((_, index) => (
            <CarouselDot 
              key={index} 
              $active={index === currentImageIndex} 
              onClick={() => setCurrentImageIndex(index)}
            />
          ))}
        </CarouselDots>
      </ModalContent>
    </Modal>
    </>
  );
};